import { motion, AnimatePresence } from 'framer-motion';
import type { WorkoutCard } from '../../lib/workoutCards';
import { getCardLabel } from '../../lib/workoutCards';
import { Button } from '../ui/Button';

/**
 * Format duration in a compact way
 */
function formatDuration(seconds: number): string {
  if (seconds === 0) return '';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return remaining > 0 ? `${minutes}m ${remaining}s` : `${minutes}m`;
}

/**
 * Format total estimated duration for all cards (excluding skipped)
 */
function formatTotalDuration(cards: WorkoutCard[]): string {
  const totalSeconds = cards
    .filter(card => !card.isSkipped)
    .reduce((sum, card) => sum + (card.autoDuration || 0), 0);
  return formatDuration(totalSeconds) || '0m';
}

/**
 * Get estimated time remaining from current card onwards (excluding skipped)
 */
function formatTimeRemaining(cards: WorkoutCard[], fromIndex: number): string {
  const remainingCards = cards.slice(fromIndex).filter(card => !card.isSkipped);
  const totalSeconds = remainingCards.reduce((sum, card) => sum + (card.autoDuration || 0), 0);
  return formatDuration(totalSeconds) || '0s';
}

interface PlaylistViewProps {
  cards: WorkoutCard[];
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (index: number) => void;
  onUnskip?: (itemId: string) => void;
  isMobile: boolean;
}

export function PlaylistView({
  cards,
  currentIndex,
  isOpen,
  onClose,
  onNavigate,
  onUnskip,
  isMobile,
}: PlaylistViewProps) {
  const handleCardClick = (index: number) => {
    onNavigate(index);
    onClose();
  };

  const getCardIcon = (card: WorkoutCard) => {
    switch (card.type) {
      case 'exercise_info':
        return (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'media':
        return (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        );
      case 'cues':
        return (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'set':
        return (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        );
      case 'rest':
        return (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const content = (
    <div className="h-full min-h-0 flex flex-col">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between p-4 border-b border-gray-200 dark:border-white/10">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Workout Playlist</h2>
          <p className="text-sm text-gray-600 dark:text-slate-400">
            {cards.length} cards • {formatTotalDuration(cards)} est.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </Button>
      </div>

      {/* Card List */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {/* Time remaining badge */}
        {currentIndex < cards.length && (
          <div className="p-4 pb-2">
            <div className="bg-primary-50 dark:bg-primary-500/10 border border-primary-200 dark:border-primary-500/30 rounded-lg p-3 text-center">
              <p className="text-xs text-primary-600 dark:text-primary-400 uppercase tracking-wide font-semibold mb-1">
                Time Remaining
              </p>
              <p className="text-2xl font-bold text-primary-700 dark:text-primary-300">
                {formatTimeRemaining(cards, currentIndex)}
              </p>
            </div>
          </div>
        )}

        <div className="p-2 pb-6 space-y-1">
          {cards.map((card, index) => {
            const isActive = index === currentIndex;
            const isPast = index < currentIndex;
            const isSkipped = card.isSkipped === true;

            return (
              <div key={card.id} className="flex items-center gap-2">
                <button
                  onClick={() => !isSkipped && handleCardClick(index)}
                  disabled={isSkipped}
                  className={`flex-1 text-left p-3 rounded-lg transition flex items-center gap-3 ${
                    isSkipped
                      ? 'bg-gray-100 dark:bg-white/5 text-gray-400 dark:text-slate-600 cursor-default'
                      : isActive
                      ? 'bg-primary-500 text-white shadow-lg'
                      : isPast
                      ? 'bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-slate-500'
                      : 'bg-gray-50 dark:bg-white/5 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-white/10'
                  }`}
                >
                  <div className={`flex-shrink-0 ${isSkipped ? 'opacity-40' : ''}`}>
                    {getCardIcon(card)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${
                      isSkipped ? 'line-through' : isActive ? 'text-white' : ''
                    }`}>
                      {getCardLabel(card)}
                    </p>
                    {isSkipped ? (
                      <p className="text-xs truncate text-gray-400 dark:text-slate-600">
                        Skipped
                      </p>
                    ) : card.autoDuration && card.autoDuration > 0 ? (
                      <p className={`text-xs truncate ${isActive ? 'text-white/80' : 'text-gray-500 dark:text-slate-500'}`}>
                        ~{formatDuration(card.autoDuration)}
                      </p>
                    ) : (
                      <p className={`text-xs truncate ${isActive ? 'text-white/80' : 'text-gray-400 dark:text-slate-600'}`}>
                        optional
                      </p>
                    )}
                  </div>

                  {!isSkipped && isPast && (
                    <svg className="w-5 h-5 flex-shrink-0 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}

                  {!isSkipped && isActive && (
                    <div className="flex-shrink-0 w-2 h-2 bg-white rounded-full animate-pulse" />
                  )}
                </button>

                {/* Unskip button for skipped exercise_info cards only */}
                {isSkipped && card.type === 'exercise_info' && onUnskip && (
                  <Button variant="ghost" size="sm" onClick={() => onUnskip(card.itemId)}>
                    Undo
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  if (!isMobile) {
    // Desktop: Show as sidebar overlay
    return (
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 bg-black/50 z-40"
            />
            <motion.div
              initial={{ x: 300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 300, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 w-80 overflow-hidden bg-white dark:bg-slate-900 shadow-2xl z-50"
            >
              {content}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    );
  }

  // Mobile: Show as bottom sheet modal
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed inset-x-0 bottom-0 h-[80dvh] max-h-[80dvh] overflow-hidden bg-white dark:bg-slate-900 rounded-t-2xl shadow-2xl z-50"
          >
            {content}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
